import { Fragment, useRef, useState } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { X } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { Badge } from '@/shared/ui/badge'
import { Switch } from '@/shared/ui/switch'
import { Label } from '@/shared/ui/label'
import { Input } from '@/shared/ui/input'
import { SearchableSelect } from '@/shared/ui/searchable-select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog'
import {
  RestoreDialog,
  type RestorePreviewInstrument,
  type RestoreSelection,
} from '@/shared/ui/restore-dialog'
import { t } from '@/shared/lib/i18n'
import {
  departmentsPath,
  deletePreviewUnitPath,
  editUnitPath,
  newBatchUnitsPath,
  newUnitPath,
  restorePreviewUnitPath,
  restoreUnitPath,
  unitPath,
  unitsPath,
  organizationPath,
} from '@/shared/lib/routes'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { useCan } from '@/shared/hooks/useCan'
import { useDebouncedCallback } from '@/shared/hooks/useDebouncedCallback'
import { Resources, Actions } from '@/shared/rbac'
import type { PaginationMeta } from '@/shared/types/PaginationMeta'
import type { WebUnit } from '@/shared/types/web'
import OrganizationTabs from '../OrganizationTabs'

interface FilterOption {
  value: number
  label: string
}

interface UnitDeletePreview {
  instruments_count: number
}

interface Props {
  units: WebUnit[]
  pagination: PaginationMeta
  sites: FilterOption[]
  departments: FilterOption[]
  searchQuery: string
  filters: { siteId: number | null; departmentId: number | null; showDeleted: boolean }
  [key: string]: unknown
}

const UnitsIndexPage = () => {
  const { units, pagination, sites, departments, searchQuery, filters } = usePage<Props>().props

  const visitWithParams = (updates: Record<string, string | null>) => {
    const params = new URLSearchParams(window.location.search)
    Object.entries(updates).forEach(([key, value]) => {
      if (value) params.set(key, value)
      else params.delete(key)
    })
    params.delete('page')
    router.visit(`${unitsPath()}?${params}`, { preserveState: true, replace: true })
  }

  useBreadcrumbs([
    { label: t('nav.administration'), href: organizationPath() },
    { label: t('nav.departmentsAndUnits'), href: departmentsPath() },
    { label: t('nav.units') },
  ])

  const searchInputRef = useRef<HTMLInputElement>(null)
  const [search, setSearch] = useState(searchQuery)

  const debouncedSearch = useDebouncedCallback((value: string) => {
    visitWithParams({ q: value.trim() || null })
  }, 300)

  const clearSearch = () => {
    setSearch('')
    debouncedSearch('')
    searchInputRef.current?.focus()
  }

  const activeFilterCount = [
    search.trim().length > 0,
    Boolean(filters.siteId),
    Boolean(filters.departmentId),
    filters.showDeleted,
  ].filter(Boolean).length

  const clearFilters = () => {
    debouncedSearch.cancel()
    setSearch('')
    router.visit(unitsPath(), { preserveState: false })
  }

  const [deletingUnit, setDeletingUnit] = useState<WebUnit | null>(null)
  const [deletePreview, setDeletePreview] = useState<UnitDeletePreview | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [restoringUnit, setRestoringUnit] = useState<WebUnit | null>(null)
  const [restorePreview, setRestorePreview] = useState<RestorePreviewInstrument[]>([])
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false)
  const [restorePreviewLoading, setRestorePreviewLoading] = useState(false)

  const isAdmin = useCan(Resources.UNIT, Actions.CREATE)
  const canDelete = useCan(Resources.UNIT, Actions.DELETE)

  const handleRestoreClick = async (unit: WebUnit) => {
    setRestoringUnit(unit)
    setRestorePreview([])
    setRestorePreviewLoading(true)
    setRestoreDialogOpen(true)
    try {
      const response = await fetch(restorePreviewUnitPath(unit.id), {
        headers: { Accept: 'application/json' },
      })
      if (response.ok) {
        const preview = (await response.json()) as RestorePreviewInstrument[]
        setRestorePreview(preview)
      }
    } catch {
      // preview stays empty
    } finally {
      setRestorePreviewLoading(false)
    }
  }

  const handleRestoreConfirm = (selection: RestoreSelection) => {
    if (!restoringUnit) return
    router.post(restoreUnitPath(restoringUnit.id), {
      instrument_ids: selection.instrumentIds,
    })
    setRestoringUnit(null)
  }

  const handleDeleteClick = async (unit: WebUnit) => {
    setDeletingUnit(unit)
    try {
      const response = await fetch(deletePreviewUnitPath(unit.id), {
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) throw new Error('Preview fetch failed')
      const preview = (await response.json()) as UnitDeletePreview
      setDeletePreview(preview)
    } catch {
      setDeletePreview(null)
    }
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (!deletingUnit) return
    router.delete(unitPath(deletingUnit.id), { preserveScroll: true })
    setDeleteDialogOpen(false)
    setDeletingUnit(null)
    setDeletePreview(null)
  }

  const deleteDialogBody = () => {
    if (!deletePreview) return t('organization.units.deleteConfirmBodyEmpty')
    const { instruments_count } = deletePreview
    if (instruments_count === 0) return t('organization.units.deleteConfirmBodyEmpty')
    return t('organization.units.deleteConfirmBody', { instrumentsCount: instruments_count })
  }

  return (
    <div className="space-y-6">
      <OrganizationTabs />
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('organization.units.title')}</h1>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <>
              <Button asChild variant="outline">
                <Link href={newBatchUnitsPath()}>{t('organization.units.newBatchUnits')}</Link>
              </Button>
              <Button asChild>
                <Link href={newUnitPath()}>{t('organization.units.newUnit')}</Link>
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative w-full sm:max-w-sm">
          <Input
            ref={searchInputRef}
            placeholder={t('organization.units.searchPlaceholder')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              debouncedSearch(e.target.value)
            }}
            className={search ? 'pr-9' : undefined}
          />
          {search && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0 h-full px-2"
              aria-label={t('organization.units.searchClearAria')}
              onClick={clearSearch}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <SearchableSelect
          className="w-48"
          value={filters.siteId ? String(filters.siteId) : 'all'}
          onValueChange={(value) => {
            visitWithParams({ site_id: value === 'all' ? null : value, department_id: null })
          }}
          placeholder={t('organization.units.filterBySite')}
          options={[
            { value: 'all', label: t('organization.units.allSites') },
            ...sites.map((site) => ({ value: String(site.value), label: site.label })),
          ]}
        />
        <SearchableSelect
          className="w-48"
          value={filters.departmentId ? String(filters.departmentId) : 'all'}
          onValueChange={(value) => {
            visitWithParams({ department_id: value === 'all' ? null : value })
          }}
          placeholder={t('organization.units.filterByDepartment')}
          options={[
            { value: 'all', label: t('organization.units.allDepartments') },
            ...departments.map((dept) => ({ value: String(dept.value), label: dept.label })),
          ]}
        />
        <div className="ml-auto flex items-center gap-2">
          <Switch
            id="units-show-deleted"
            checked={filters.showDeleted}
            onCheckedChange={(next) => visitWithParams({ show_deleted: next ? '1' : null })}
          />
          <Label htmlFor="units-show-deleted">{t('organization.units.showDeleted')}</Label>
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="ml-2 text-muted-foreground"
              onClick={clearFilters}
            >
              {t('organization.units.clearFilters')}
            </Button>
          )}
        </div>
      </div>

      {/* Mobile: card list */}
      <div className="space-y-2 lg:hidden">
        {units.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-sm text-muted-foreground">{t('organization.units.empty')}</p>
          </div>
        ) : (
          units.map((unit) => (
            <div
              key={unit.id}
              className={`rounded-lg border bg-card p-3 shadow-sm space-y-2${unit.discarded ? ' opacity-60' : ''}`}
            >
              <div className="flex items-center gap-2">
                <span className="font-medium">{unit.name}</span>
                {unit.discarded && (
                  <Badge variant="secondary">{t('organization.units.deleted')}</Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {[unit.departmentName, unit.siteName].filter(Boolean).join(' · ') || '—'}
              </p>
              {unit.licenseNumber && (
                <p className="text-xs text-muted-foreground">{unit.licenseNumber}</p>
              )}
              {isAdmin && (
                <>
                  {unit.discarded && canDelete && unit.departmentDiscarded ? (
                    <p className="pt-1 text-xs text-destructive">
                      {t('organization.units.restoreBlockedDepartmentDeleted')}
                    </p>
                  ) : (
                    <div className="flex justify-end gap-2 pt-1">
                      {unit.discarded ? (
                        canDelete && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => void handleRestoreClick(unit)}
                          >
                            {t('organization.units.restore')}
                          </Button>
                        )
                      ) : (
                        <>
                          <Button asChild variant="outline" size="sm">
                            <Link href={editUnitPath(unit.id)}>{t('organization.units.edit')}</Link>
                          </Button>
                          {canDelete && !unit.isLastActiveUnit && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => void handleDeleteClick(unit)}
                            >
                              {t('organization.units.delete')}
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          ))
        )}
      </div>

      {/* Desktop: table */}
      <div className="hidden rounded-md border lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('organization.units.table.name')}</TableHead>
              <TableHead>{t('organization.units.table.department')}</TableHead>
              <TableHead>{t('organization.units.table.site')}</TableHead>
              <TableHead>{t('organization.units.table.licenseNumber')}</TableHead>
              {isAdmin && (
                <TableHead className="w-[1%] text-right">
                  {t('organization.units.table.actions')}
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {units.map((unit) => {
              const restoreBlocked = Boolean(
                unit.discarded && canDelete && unit.departmentDiscarded,
              )

              return (
                <Fragment key={unit.id}>
                  <TableRow
                    className={`${unit.discarded ? 'opacity-60' : ''}${restoreBlocked ? ' border-b-0' : ''}`}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {unit.name}
                        {unit.discarded && (
                          <Badge variant="secondary">{t('organization.units.deleted')}</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{unit.departmentName ?? '—'}</TableCell>
                    <TableCell>{unit.siteName ?? '—'}</TableCell>
                    <TableCell>{unit.licenseNumber ?? '—'}</TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {unit.discarded ? (
                            canDelete &&
                            (unit.departmentDiscarded ? (
                              <span className="text-muted-foreground">—</span>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => void handleRestoreClick(unit)}
                              >
                                {t('organization.units.restore')}
                              </Button>
                            ))
                          ) : (
                            <>
                              <Button asChild variant="outline" size="sm">
                                <Link href={editUnitPath(unit.id)}>
                                  {t('organization.units.edit')}
                                </Link>
                              </Button>
                              {canDelete && !unit.isLastActiveUnit && (
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => void handleDeleteClick(unit)}
                                >
                                  {t('organization.units.delete')}
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                  {restoreBlocked && (
                    <TableRow className="opacity-60 hover:bg-transparent">
                      <TableCell
                        colSpan={isAdmin ? 5 : 4}
                        className="pt-0 text-xs text-destructive"
                      >
                        {t('organization.units.restoreBlockedDepartmentDeleted')}
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              )
            })}
          </TableBody>
        </Table>
      </div>

      <RestoreDialog
        open={restoreDialogOpen}
        onOpenChange={setRestoreDialogOpen}
        resourceName={restoringUnit?.name ?? ''}
        loading={restorePreviewLoading}
        instruments={restorePreview}
        onConfirm={handleRestoreConfirm}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('organization.units.deleteConfirmTitle')}: {deletingUnit?.name}
            </AlertDialogTitle>
            <AlertDialogDescription>{deleteDialogBody()}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('organization.units.form.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('organization.units.deleteConfirmAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          {pagination.totalPages > 1 &&
            `${t('organization.units.paginationPage')} ${pagination.page} ${t('organization.units.paginationOf')} ${pagination.totalPages} (`}
          {pagination.total} {t('organization.units.paginationNoun')}
          {pagination.totalPages > 1 && ')'}
        </span>
        <div className="flex gap-2">
          {pagination.page > 1 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const params = new URLSearchParams(window.location.search)
                params.set('page', String(pagination.page - 1))
                router.visit(`${unitsPath()}?${params}`, { preserveState: true })
              }}
            >
              {t('organization.units.previous')}
            </Button>
          )}
          {pagination.page < pagination.totalPages && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const params = new URLSearchParams(window.location.search)
                params.set('page', String(pagination.page + 1))
                router.visit(`${unitsPath()}?${params}`, { preserveState: true })
              }}
            >
              {t('organization.units.next')}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default UnitsIndexPage
